/**
 * The original email + password sign-in form, lifted out of PageLogin.
 *
 * It is kept, not deleted: REACT_APP_PASSWORD_LOGIN_ENABLED (and the backend's
 * PASSWORD_LOGIN_ENABLED) can put it back in front of customers in a rebuild if
 * the passwordless channels have a bad day. See utils/featureFlags.ts.
 */

import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import Input from 'shared/Input/Input';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import showLoader from 'components/LoaderComponent';
import { AppDispatch } from 'reduxStore/store';
import { customerLogin } from '../../reduxStore/store/user';
import { authErrorMessage, isPasswordLoginDisabledError } from 'service/authApi';
import useAuthSession from 'hooks/useAuthSession';

type FormValues = {
    email: string;
    password: string;
};

const validationScheme = yup.object().shape({
    email: yup.string().email('Must be a valid email.').required('Email is required'),
    password: yup.string().required('Password is required.')
});

export interface PasswordLoginFormProps {
    className?: string;
    /**
     * Fired when the API answers 403 `{ passwordLoginDisabled: true }` - the
     * server flag is off even though this build still shows the form. The page
     * uses it to take the form away and point at the one-time code flow.
     */
    onPasswordLoginDisabled?: (message: string) => void;
}

const PasswordLoginForm: FC<PasswordLoginFormProps> = ({ className = '', onPasswordLoginDisabled }) => {
    const dispatch = useDispatch<AppDispatch>();
    const establishSession = useAuthSession();
    const [buttonStatus, setButtonStatus] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<FormValues>({
        mode: 'onChange',
        resolver: yupResolver(validationScheme)
    });

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setButtonStatus(true);
        showLoader(true);

        try {
            const session = await dispatch(
                customerLogin({
                    email: data?.email,
                    password: data?.password
                })
            ).unwrap();

            await establishSession(session);
        } catch (err) {
            // The flag can be flipped on the server at any moment, including
            // between this build shipping and someone typing their password.
            if (isPasswordLoginDisabledError(err)) {
                const message = authErrorMessage(err, 'Password sign-in is no longer available. Please use a one-time code instead.');
                toast.info(message);
                onPasswordLoginDisabled?.(message);
            } else {
                toast.error(authErrorMessage(err, 'Something went wrong!'));
            }
        } finally {
            setButtonStatus(false);
            showLoader(false);
        }
    };

    return (
        <Form className={`grid grid-cols-1 gap-6 ${className}`} action="#" method="post" onSubmit={handleSubmit(onSubmit)}>
            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">Email address</span>
                <Input
                    type="email"
                    autoComplete="email"
                    placeholder="example@example.com"
                    className="mt-1"
                    style={{ borderColor: errors.email ? '#ea5455' : '' }}
                    {...register('email')}
                />
                {errors && errors.email && <FormFeedback style={{ color: '#ea5455' }}>{errors.email.message}</FormFeedback>}
            </label>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Password
                    <Link to="/forgot-password" className="text-sm text-green-600">
                        Forgot password?
                    </Link>
                </span>
                <Input
                    type="password"
                    autoComplete="current-password"
                    style={{ borderColor: errors.password ? '#ea5455' : '' }}
                    className="mt-1"
                    {...register('password')}
                />
                {errors && errors.password && <FormFeedback style={{ color: '#ea5455' }}>{errors.password.message}</FormFeedback>}
            </label>
            <ButtonPrimary type="submit" disabled={buttonStatus} loading={buttonStatus}>
                Continue
            </ButtonPrimary>
        </Form>
    );
};

export default PasswordLoginForm;
